import React from 'react';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { defineMessages, useIntl } from 'react-intl';
import FhirDateTime from '../../../../../../util/fhirResourceParsers/FhirDateTime';
import useFhirSearch, {
  Hits,
} from '../../../../../Surveys/util/FhirSearch/useFhirSearch';
import { ConditionAttachment, getConditionCodeDisplay } from '../Attachment';

const dateTimeParser = new FhirDateTime();

const messages = defineMessages({
  code: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.conditionViewModal.code',
    defaultMessage: 'Code',
  },
  clinicalStatus: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.conditionViewModal.clinicalStatus',
    defaultMessage: 'Clinical Status',
  },
  verificationStatus: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.conditionViewModal.verificationStatus',
    defaultMessage: 'Verification Status',
  },
  onset: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.conditionViewModal.onset',
    defaultMessage: 'Onset',
  },
  category: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.conditionViewModal.category',
    defaultMessage: 'Category',
  },
});

const useStyles = makeStyles(theme => ({
  content: {
    padding: theme.spacing(2),
  },
  field: {
    marginBottom: theme.spacing(1.5),
  },
  value: {
    whiteSpace: 'normal',
    wordWrap: 'break-word',
  },
}));

type ConditionViewModalProps = {
  attachment: ConditionAttachment;
  onDismiss: () => void;
};

const ConditionField = ({
  label,
  value,
}: {
  label: string;
  value?: string;
}) => {
  const classes = useStyles();
  return (
    <Box direction="column" className={classes.field}>
      <Text weight="bold">{label}</Text>
      <Text className={classes.value}>{value || '-'}</Text>
    </Box>
  );
};

const getOnsetDisplay = (resource: fhir.Condition): string | undefined => {
  if (resource.onsetDateTime) {
    return dateTimeParser.getDisplayValue(resource.onsetDateTime) || undefined;
  }
  if (resource.onsetPeriod?.start) {
    return (
      dateTimeParser.getDisplayValue(resource.onsetPeriod.start) || undefined
    );
  }
  return resource.onsetString;
};

const getCodeableConceptDisplay = (concept: unknown): string | undefined => {
  if (!concept) {
    return undefined;
  }

  if (typeof concept === 'string') {
    return concept;
  }

  if (typeof concept !== 'object') {
    return undefined;
  }

  const codeableConcept = concept as {
    coding?: Array<{ code?: string; display?: string }>;
    text?: string;
  };

  return (
    codeableConcept.text ||
    codeableConcept.coding?.find(coding => coding.display)?.display ||
    codeableConcept.coding?.[0]?.code
  );
};

const getCategoryDisplay = (resource: fhir.Condition): string | undefined =>
  resource.category?.[0]?.coding?.[0]?.display ||
  resource.category?.[0]?.coding?.[0]?.code ||
  resource.category?.[0]?.text;

export const ConditionViewModal = ({
  attachment,
  onDismiss,
}: ConditionViewModalProps) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const [response, isLoading] = useFhirSearch<Hits<fhir.Condition>>({
    type: 'select',
    columns: '*',
    from: [{ table: 'condition' }],
    limit: [
      { type: 'number', value: 0 },
      { type: 'number', value: 1 },
    ],
    where: {
      type: 'elasticsearch',
      query: {
        bool: {
          must: [
            {
              term: {
                'id.keyword': attachment.resource.id,
              },
            },
          ],
        },
      },
    },
  });

  const condition = response?.hits?.hits[0]?._source ?? attachment.resource;
  const title = getConditionCodeDisplay(condition);

  return (
    <Modal
      isOpen={true}
      size={1}
      fullWidth
      onDismiss={onDismiss}
      title={title}
      data-testid="condition-view-modal"
    >
      {isLoading ? (
        <Box fullWidth justify="center" align="center" padding={4}>
          <DotLoader />
        </Box>
      ) : (
        <Box direction="column" className={classes.content}>
          <ConditionField
            label={formatMessage(messages.code)}
            value={getConditionCodeDisplay(condition)}
          />
          <ConditionField
            label={formatMessage(messages.clinicalStatus)}
            value={getCodeableConceptDisplay(condition.clinicalStatus)}
          />
          <ConditionField
            label={formatMessage(messages.verificationStatus)}
            value={getCodeableConceptDisplay(condition.verificationStatus)}
          />
          <ConditionField
            label={formatMessage(messages.onset)}
            value={getOnsetDisplay(condition)}
          />
          <ConditionField
            label={formatMessage(messages.category)}
            value={getCategoryDisplay(condition)}
          />
        </Box>
      )}
    </Modal>
  );
};
